import * as React from "react";
import * as moment from "moment";
import { useSelector } from "react-redux";

import { Box, Typography } from "@mui/material";
import { t } from "../../translate";
import { RootState } from "../../../store";
import classNames from "../../../helpers/classNames";
import * as calFns from "../functions/calendarFunctions";
import * as calendarData from "../functions/calendarData";

import styles from "./styles.module.css";

interface MonthViewProps {
  onDayClicked: (date: Date) => void;
  viewBsYear: number;
  viewBsMonth: number;
  defaultActiveDate: Date;
  maxValidDate?: Date;
  minValidDate?: Date;
  style?: React.CSSProperties;
  showHolidayInRed;
}

type DayInfo = ReturnType<typeof calFns.convertADtoBS> & { adDate: Date };

const MonthView: React.FC<MonthViewProps> = ({
  onDayClicked,
  viewBsYear,
  viewBsMonth,
  defaultActiveDate,
  maxValidDate = "",
  minValidDate = "",
  style = {},
  showHolidayInRed = true
}) => {
  const [selectedDate, setSelectedDate] = React.useState<Date>(defaultActiveDate || new Date());
  const rcHolidays = useSelector(
    (state: RootState) => state.userContext.resourceCentre?.holidays || {}
  );
  const today = new Date();
  const todayBS = calFns.convertADtoBS(today);
  const [bsYear] = React.useState(todayBS.bsYear);
  const [yearlyHolidays] = React.useState(rcHolidays[String(bsYear)] || []);

  React.useEffect(() => {
    setSelectedDate(defaultActiveDate || new Date());
  }, [defaultActiveDate]);

  const onDaySelect = (date: Date) => {
    setSelectedDate(date);
    onDayClicked(date);
  };

  const getDayInfo = (date: Date): DayInfo => {
    const bsDate = calFns.convertADtoBS(date);
    return { adDate: new Date(date), ...bsDate };
  };

  const getDays = (): DayInfo[] => {
    const startDay = calFns.convertBStoAD(viewBsYear, viewBsMonth, 1);
    startDay.setDate(startDay.getDate() - startDay.getDay());
    const lastDay = calFns.convertBStoAD(
      viewBsYear,
      viewBsMonth,
      calFns.getBsMonthDays(viewBsYear, viewBsMonth)
    );
    lastDay.setDate(lastDay.getDate() + (6 - lastDay.getDay()));
    const days: DayInfo[] = [];
    const current = new Date(startDay);
    while (current <= lastDay) {
      days.push(getDayInfo(current));
      current.setDate(current.getDate() + 1);
    }
    return days;
  };

  const isDateDisabled = (adDate: Date) => {
    if (maxValidDate) {
      const disabled = moment(adDate).isAfter(moment(maxValidDate), "day");
      if (disabled) return disabled;
    }
    if (minValidDate) {
      const disabled = moment(adDate).isBefore(moment(minValidDate), "day");
      if (disabled) return disabled;
    }
    return false;
  };
  const isSameDate = (adDate: Date, toMatch: Date = new Date()) =>
    adDate.getDate() === toMatch.getDate() &&
    adDate.getMonth() === toMatch.getMonth() &&
    adDate.getFullYear() === toMatch.getFullYear();

  const isHoliday = (adDate: Date) =>
    yearlyHolidays.some((h) => h.date.slice(0, 10) === adDate.toISOString().slice(0, 10));

  return (
    <div className={`r-n-cal-month-view ${styles.calendar}`}>
      <div className={`r-n-cal-weekdays ${styles.weekdays}`}>
        {calendarData.bsDays(t).map((day) => (
          <div key={day} className={styles.weekday}>
            {day}
          </div>
        ))}
      </div>
      <div className={`r-n-cal-days ${styles.days}`} style={style}>
        {getDays().map(({ adDate, bsDate, bsMonth }: any, i: number) => (
          <button
            type="button"
            className={classNames("r-n-cal-day", styles.day, {
              [styles.weekend]: i % 7 === 6,
              [styles.dayMuted]: bsMonth !== viewBsMonth,
              [styles.today]: isSameDate(adDate),
              [styles.selectedDay]: isSameDate(adDate, selectedDate),
              [styles.disabled]: isDateDisabled(adDate),
              [styles.holiday]: showHolidayInRed && isHoliday(adDate)
            } as any)}
            key={`${bsDate} ${bsMonth}`}
            onClick={() => {
              if (!isDateDisabled(adDate)) {
                onDaySelect(adDate);
              }
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "baseline",
                borderRadius: "50%",
                width: "28px",
                height: "28px",
                gap: "2px"
              }}
            >
              <Typography>{calFns.toDevanagariDigits(bsDate)}</Typography>
              <Typography fontSize={8}>{moment(adDate).format("D")}</Typography>
            </Box>
          </button>
        ))}
      </div>
    </div>
  );
};

export default MonthView;
